/**
 * 钉钉免登页面 - 简化版
 * 所有钉钉登录逻辑都在这个文件里，不依赖复杂的平台适配器
 */

import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { dingTalkLogin } from '@/lib/api/user';
import { setToken } from '@/lib/auth';
import { safeToast } from '@/lib/toast';
import { useI18n } from '@/hooks/useI18n';
import { useIframeHeightSync } from '@/hooks/useIframeHeightSync';
import styled from 'styled-components';
import { handleLoginRedirect } from '@/lib/loginRedirect';

// ============= 样式组件 =============
const DingTalkLoginContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  background: #ffffff;
  padding: 20px;
`;

const LoadingCard = styled.div`
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  padding: 40px;
  text-align: center;
  max-width: 400px;
  width: 100%;
`;

const LoadingText = styled.div`
  margin-top: 20px;
  font-size: 16px;
  color: #666;
  font-weight: 500;
`;

const ErrorText = styled.div`
  margin-top: 20px;
  font-size: 16px;
  color: #ff4757;
  font-weight: 500;
`;

const RetryButton = styled.button`
  margin-top: 20px;
  padding: 12px 24px;
  color: blue;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.3s ease;

  &:hover {
    background: #5a6fd8;
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`;

const LoaderWrapper = styled.div`
  .loader {
    height: 120px;
    width: 120px;
    display: flex;
    justify-content: center;
    align-items: center;
    position: relative;
    margin: 0 auto;
  }

  .loader div {
    height: 30px;
    width: 30px;
    position: absolute;
    animation: move 4s infinite;
  }

  .loader div:nth-child(1) {
    background-color: rgb(158, 136, 246);
    box-shadow: rgb(158, 136, 246) 0px 7px 29px 0px;
    transform: translate(-30px,-30px);
    animation-delay: -1s;
  }

  .loader div:nth-child(2) {
    background-color: rgb(97, 183, 253);
    box-shadow: rgb(97, 183, 253) 0px 7px 29px 0px;
    transform: translate(30px,-30px);
    animation-delay: -2s;
  }

  .loader div:nth-child(3) {
    background-color: rgb(95, 249, 175);
    box-shadow: rgb(95, 249, 175) 0px 7px 29px 0px;
    transform: translate(30px,30px);
    animation-delay: -3s;
  }

  .loader div:nth-child(4) {
    background-color: rgb(243, 171, 89);
    box-shadow: rgb(243, 171, 89) 0px 7px 29px 0px;
    transform: translate(-30px,30px);
    animation-delay: -4s;
  }

  @keyframes move {
    0% {
      transform: translate(-30px, -30px);
    }
    25% {
      transform: translate(30px, -30px);
    }
    50% {
      transform: translate(30px, 30px);
    }
    75% {
      transform: translate(-30px, 30px);
    }
    100% {
      transform: translate(-30px, -30px);
    }
  }
`;

const NotDingTalkWarning = styled.div`
  margin-top: 20px;
  padding: 16px;
  background: #fff3cd;
  border: 1px solid #ffc107;
  border-radius: 8px;
  color: #856404;
  font-size: 14px;
  line-height: 1.6;
`;

const HiddenIframe = styled.iframe`
  display: none;
  position: absolute;
  width: 0;
  height: 0;
  border: none;
`;

// ============= 钉钉工具函数 =============

/**
 * 检测是否是钉钉环境
 */
function isDingTalkEnv(): boolean {
  const ua = navigator.userAgent.toLowerCase();
  const isGdt = ua.includes('taurusapp'); // 排除专有钉钉
  const isDingTalk = ua.includes('dingtalk');
  return isDingTalk && !isGdt;
}

/**
 * 检测当前页面是否在iframe中
 */
function isInIframe(): boolean {
  try {
    return window.self !== window.top;
  } catch (e) {
    return true;
  }
}

/**
 * 检测父窗口是否是允许的钉钉域名
 */
function isAllowedParentDomain(): boolean {
  if (!isInIframe()) {
    return false;
  }

  try {
    const parentOrigin = document.referrer;
    const allowedDomains = [
      'https://n.dingtalk.com',
      'https://pre-n.dingtalk.com'
    ];

    return allowedDomains.some(domain => parentOrigin.startsWith(domain));
  } catch (e) {
    console.error('❌ 无法获取父窗口域名', e);
    return false;
  }
}

/**
 * 等待window.dd对象可用
 */
function waitForDingTalkSDK(timeout = 5000): Promise<void> {
  return new Promise((resolve, reject) => {
    const startTime = Date.now();

    const checkDD = () => {
      if (window.dd) {
        console.log('✅ window.dd 对象已就绪');
        resolve();
        return;
      }

      if (Date.now() - startTime > timeout) {
        reject(new Error('等待钉钉SDK超时'));
        return;
      }

      // 每50ms检查一次
      setTimeout(checkDD, 50);
    };

    checkDD();
  });
}

/**
 * 加载钉钉SDK
 */
function loadDingTalkSDK(): Promise<void> {
  return new Promise((resolve, reject) => {
    const sdkUrl = 'https://g.alicdn.com/dingding/dingtalk-jsapi/2.10.3/dingtalk.open.js';

    // 检查是否已经加载
    if (window.dd) {
      console.log('✅ 钉钉SDK已存在');
      resolve();
      return;
    }

    // 检查script标签是否存在
    const existingScript = document.querySelector(`script[src="${sdkUrl}"]`);
    if (existingScript) {
      console.log('⏳ 钉钉SDK脚本已加载，等待初始化...');
      // script存在但window.dd还没准备好，等待它
      waitForDingTalkSDK()
          .then(resolve)
          .catch(reject);
      return;
    }

    console.log('📦 开始加载钉钉SDK...');
    const script = document.createElement('script');
    script.src = sdkUrl;
    script.async = true;

    script.onload = () => {
      console.log('📥 钉钉SDK脚本加载完成，等待初始化...');
      // 脚本加载完成，等待window.dd对象可用
      waitForDingTalkSDK()
          .then(() => {
            console.log('✅ 钉钉SDK完全就绪');
            resolve();
          })
          .catch(reject);
    };

    script.onerror = (error) => {
      console.error('❌ 钉钉SDK加载失败', error);
      reject(new Error('钉钉SDK加载失败'));
    };

    document.head.appendChild(script);
  });
}

/**
 * 获取钉钉免登授权码（标准方式）
 */
function getDingTalkAuthCode(corpId: string): Promise<string> {
  return new Promise((resolve, reject) => {
    if (!window.dd) {
      const error = new Error('钉钉SDK未加载，window.dd对象不存在');
      console.error('❌', error.message);
      reject(error);
      return;
    }

    if (!window.dd.runtime?.permission?.requestAuthCode) {
      const error = new Error('钉钉SDK方法不可用');
      console.error('❌', error.message);
      reject(error);
      return;
    }

    // 定义多个应用的clientId，按顺序尝试
    const clientIdList = [
      { name: '授客AI', clientId: 'suitezawadsugvy0xgkwq' },
      { name: '授客AI教练', clientId: 'suite0pbn2jbdlezc96wq' },
      { name: '授客学堂预发版', clientId: 'suiterjxxkfi0yljvagw6' },
      { name: '授客AI教练预发', clientId: 'suiteqqke29tocag91kb3' },
    ];

    console.log('📱 开始获取钉钉授权码（标准方式），corpId =>', corpId);
    console.log('🔄 将按顺序尝试', clientIdList.length, '个应用的clientId');

    let currentIndex = 0;

    // 递归尝试每个clientId
    const tryNextClient = () => {
      if (currentIndex >= clientIdList.length) {
        // 所有clientId都尝试失败
        const error = new Error('所有应用的clientId都尝试失败，无法获取授权码');
        console.error('❌', error.message);
        reject(error);
        return;
      }

      const currentClient = clientIdList[currentIndex];
      console.log(
          `📌 [${currentIndex + 1}/${clientIdList.length}] 尝试应用: ${currentClient.name}，clientId: ${currentClient.clientId}`
      );

      try {
        window.dd.runtime.permission.requestAuthCode({
          corpId: corpId,
          clientId: currentClient.clientId,
          onSuccess: (result: any) => {
            console.log(
                `✅ [${currentClient.name}] 获取钉钉授权码成功！授权码: ${result.code}`
            );
            console.log('🎯 使用的应用:', currentClient.name);
            resolve(result.code);
          },
          onFail: (error: any) => {
            console.warn(
                `⚠️ [${currentClient.name}] 获取授权码失败 =>`,
                error?.message || error
            );
            // 失败后尝试下一个clientId
            currentIndex++;
            console.log('🔄 准备尝试下一个应用...');
            tryNextClient();
          },
        });
      } catch (error: any) {
        console.error(
            `❌ [${currentClient.name}] 调用钉钉API异常 =>`,
            error?.message || error
        );
        // 异常后也尝试下一个clientId
        currentIndex++;
        console.log('🔄 发生异常，准备尝试下一个应用...');
        tryNextClient();
      }
    };

    // 开始尝试第一个clientId
    tryNextClient();
  });
}

/**
 * 通过postMessage从父窗口获取钉钉授权码（iframe嵌入场景）
 */
function getAuthCodeFromParent(corpId: string): Promise<string> {
  return new Promise((resolve, reject) => {
    if (!window.parent || window.parent === window) {
      reject(new Error('当前不在iframe中'));
      return;
    }

    console.log('📱 开始通过postMessage获取钉钉授权码，corpId =>', corpId);

    // 生成唯一的消息ID
    const messageId = `ai_one_${Date.now()}`;

    // 创建消息处理器
    const messageHandler = (event: MessageEvent) => {
      try {
        console.log('📩 收到父窗口消息 event:', event);

        // 验证消息来源
        if (event.data.messageId === messageId) {
          const { success, data } = event.data.result;

          if (success) {
            console.log('✅ 从父窗口获取授权码成功 =>', data.code);
            resolve(data.code);
          } else {
            console.error('❌ 父窗口返回授权码失败');
            reject(data);
          }

          // 移除事件监听器
          window.removeEventListener('message', messageHandler);
        }
      } catch (error) {
        console.error('❌ 处理父窗口消息异常 =>', error);
      }
    };

    // 添加消息监听器
    window.addEventListener('message', messageHandler);

    // 向父窗口发送请求
    const targetWindow = window.parent;
    targetWindow?.postMessage(
        {
          methodName: 'runtime.permission.requestAuthCode',
          params: { corpId },
          messageId,
        },
        '*'
    );

    console.log('📤 已向父窗口发送授权码请求', { messageId, corpId });

    // 设置超时
    setTimeout(() => {
      window.removeEventListener('message', messageHandler);
      reject(new Error('获取授权码超时'));
    }, 30000); // 30秒超时
  });
}

// 声明全局dd对象
declare global {
  interface Window {
    dd: any;
  }
}

// ============= 主组件 =============

/**
 * 钉钉免登页面
 *
 * 使用方式：
 * 1. 直接访问：/dingtalk-login
 * 2. 带重定向：/dingtalk-login?redirect=/some-page
 * 3. 带路由名称：/dingtalk-login?routeName=taskDetail&routeParams={"id":"123"}
 * 4. 跳转到后台：/dingtalk-login?target=admin&redirect=/users
 */
export default function DingTalkLogin() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useI18n();

  // 使用iframe高度自适应hook
  useIframeHeightSync({
    minHeight: 400, // 设置一个合理的最小高度
    enabled: true,
    debounceDelay: 800, // 增加防抖延迟，避免动画期间频繁触发
  });

  // 状态管理
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [loginStatus, setLoginStatus] = useState<string>('正在初始化...');
  const [isDingTalk, setIsDingTalk] = useState(false);

  // SSO Bridge iframe ref
  const ssoBridgeRef = useRef<HTMLIFrameElement>(null);
  const [ssoBridgeReady, setSsoBridgeReady] = useState(false);

  // 从环境变量或URL参数获取corpId，并移除末尾的 _ai 标识
  const corpId = searchParams.get('corpId') || process.env.REACT_APP_DINGTALK_CORP_ID || '';
  // const rawCorpId = searchParams.get('corpId') || process.env.REACT_APP_DINGTALK_CORP_ID || '';
  // const corpId = rawCorpId.replace(/_ai$/, '');

  // SSO Bridge URL
  const SSO_BRIDGE_URL = process.env.REACT_APP_SSO_BRIDGE_URL || 'http://172.16.0.74:8004/sso-bridge.html';

  /**
   * 监听来自 SSO Bridge 的消息
   */
  useEffect(() => {
    const handleSSOMessage = (event: MessageEvent) => {
      const data = event.data;

      switch (data.type) {
        case 'SSO_BRIDGE_READY':
          console.log('✅ SSO Bridge 已就绪');
          setSsoBridgeReady(true);
          break;

        case 'SSO_BRIDGE_SUCCESS':
          console.log('✅ 登录数据同步到B系统成功', {
            storedCount: data.storedCount,
            timestamp: data.timestamp
          });
          break;

        case 'SSO_BRIDGE_ERROR':
          console.error('❌ SSO Bridge 同步失败', data.error);
          break;

        default:
          break;
      }
    };

    window.addEventListener('message', handleSSOMessage);

    return () => {
      window.removeEventListener('message', handleSSOMessage);
    };
  }, []);

  /**
   * 同步登录数据到 B 系统 (SSO Bridge)
   */
  const syncLoginDataToBSystem = useCallback((loginData: any) => {
    if (!ssoBridgeRef.current?.contentWindow) {
      console.warn('⚠️ SSO Bridge iframe 未加载，跳过同步');
      return;
    }

    if (!ssoBridgeReady) {
      console.warn('⚠️ SSO Bridge 未就绪，延迟同步...');
      setTimeout(() => syncLoginDataToBSystem(loginData), 1000);
      return;
    }

    console.log('📤 开始同步登录数据到 B 系统...');

    try {
      ssoBridgeRef.current.contentWindow.postMessage(
          {
            type: 'SSO_LOGIN_DATA',
            payload: loginData
          },
          '*'
      );

      console.log('✅ 登录数据已发送到 SSO Bridge');
    } catch (error) {
      console.error('❌ 发送登录数据到 SSO Bridge 失败', error);
    }
  }, [ssoBridgeReady]);


  /**
   * 完整的登录流程
   */
  const performLogin = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // 步骤1: 检测环境
      console.log('🔍 步骤1: 检测钉钉环境...');
      setLoginStatus('正在检测环境...');
      const isDingTalkEnvResult = isDingTalkEnv();
      setIsDingTalk(isDingTalkEnvResult);

      if (!isDingTalkEnvResult) {
        setError('请在钉钉环境中打开');
        setLoading(false);
        return;
      }

      if (!corpId) {
        setError('缺少企业ID配置（corpId）');
        setLoading(false);
        return;
      }

      // 步骤2: 判断是否在允许的iframe中
      const inAllowedIframe = isInIframe() && isAllowedParentDomain();
      console.log(`🔍 步骤2: 检测iframe环境 => ${inAllowedIframe ? '是（使用postMessage方式）' : '否（使用标准方式）'}`);

      let authCode: string;

      if (inAllowedIframe) {
        // iframe场景：通过postMessage获取授权码
        console.log('📱 步骤3: 通过postMessage获取授权码...');
        setLoginStatus('正在通过父窗口获取授权码...');
        // 现在数据库存的企业id拼接了_ai,调用钉钉能力的时候要移除
        const dingCorpId = corpId.replace(/_ai$/, '');
        authCode = await getAuthCodeFromParent(dingCorpId);
        console.log('✅ 从父窗口获取到授权码 =>', authCode);
      } else {
        // 标准场景：加载SDK并直接调用
        console.log('📦 步骤3a: 加载钉钉SDK...');
        setLoginStatus('正在加载钉钉SDK...');
        await loadDingTalkSDK();

        console.log('📱 步骤3b: 获取授权码...');
        setLoginStatus('正在获取授权码...');
        // 现在数据库存的企业id拼接了_ai,调用钉钉能力的时候要移除
        const dingCorpId = corpId.replace(/_ai$/, '');
        authCode = await getDingTalkAuthCode(dingCorpId);
        console.log('✅ 获取到授权码 =>', authCode);
      }

      // 步骤4: 调用后端登录接口
      console.log('🔐 步骤4: 调用后端登录接口...');
      setLoginStatus('正在验证授权码...');
      const response = await dingTalkLogin(authCode, corpId);

      console.log('📥 后端响应 =>', response);

      if (response.success && response.data) {
        // 保存JWT token
        setToken(response.data.access_token);
        // 设置登录类型为钉钉应用登录
        localStorage.setItem('loginType', 'ding_app');

        console.log('✅ 登录成功，token已保存');

        // 保存到A系统的localStorage
        const sokeInfo = {
          "soke_jwt": response.data.soke_jwt,
          "user_info": response.data.user_info,
          [`Authorization:${response.data.user_info?.corp_id}`]: response?.data.soke_jwt,
          "Authorization": response?.data.soke_jwt,
          "corpId": response.data.user_info?.corp_id
        };
        localStorage.setItem("sokeInfo", JSON.stringify(sokeInfo));

        // 同步到B系统
        const loginDataToSync = {
          soke_jwt: response.data.soke_jwt,
          user_info: JSON.stringify(response.data.user_info),
          [`Authorization:${response.data.user_info?.corp_id}`]: response?.data.soke_jwt,
          Authorization: response?.data.soke_jwt,
          corpId: response.data.user_info?.corp_id
        };
        syncLoginDataToBSystem(loginDataToSync);

        setLoginStatus('登录成功，正在跳转...');

        // 延迟后跳转
        setTimeout(() => {
          handleLoginRedirect({
            searchParams,
            navigate,
            isDingTalk,
          });
        }, 500);
      } else {
        const errorMessage = response.errorMessage || '钉钉登录失败';
        console.error('❌ 登录失败 =>', errorMessage);
        setError(errorMessage);
        safeToast.error(errorMessage);
      }
    } catch (error: any) {
      const errorMessage = error.message || '钉钉登录异常，请重试';
      console.error('❌ 登录异常 =>', error);
      setError(errorMessage);
      safeToast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [navigate, searchParams, corpId, isDingTalk, syncLoginDataToBSystem]);

  /**
   * 重试登录
   */
  const handleRetry = async () => {
    await performLogin();
  };

  /**
   * 页面加载时自动执行登录
   */
  useEffect(() => {
    console.log('========================================');
    console.log('🚀 钉钉免登页面初始化');
    console.log('========================================');

    performLogin();
  }, []); // 只在组件挂载时执行一次

  return (
      <DingTalkLoginContainer>
        {/* 隐藏的 SSO Bridge iframe */}
        <HiddenIframe
            ref={ssoBridgeRef}
            src={SSO_BRIDGE_URL}
            title="SSO Bridge"
        />

        <LoadingCard>
          <LoaderWrapper>
            <div className="loader">
              <div />
              <div />
              <div />
              <div />
            </div>
          </LoaderWrapper>

          {/* 非钉钉环境提示 */}
          {!isDingTalk && !loading && (
              <NotDingTalkWarning>
                <strong>⚠️ 提示</strong>
                <br />
                此页面需要在钉钉环境中打开。
                <br />
                请在钉钉客户端中访问此链接。
              </NotDingTalkWarning>
          )}

          {/* 加载状态 */}
          {loading && (
              <LoadingText>
                {false&&loginStatus}
              </LoadingText>
          )}

          {/* 错误提示 */}
          {error && (
              <>
                <ErrorText>{error}</ErrorText>
                <RetryButton onClick={handleRetry} disabled={loading}>
                  {t('common.retry')}
                </RetryButton>
              </>
          )}

          {/* 调试信息（开发环境） */}
          {process.env.NODE_ENV === 'development' && (
              <div style={{
                marginTop: '20px',
                padding: '10px',
                background: '#f5f5f5',
                borderRadius: '8px',
                fontSize: '12px',
                textAlign: 'left',
                color: '#666',
              }}>
                <div><strong>调试信息：</strong></div>
                <div>钉钉环境: {isDingTalk ? '是' : '否'}</div>
                <div>iframe环境: {isInIframe() ? '是' : '否'}</div>
                <div>允许的父域名: {isAllowedParentDomain() ? '是' : '否'}</div>
                <div>CorpId: {corpId || '未配置'}</div>
                <div>加载中: {loading ? '是' : '否'}</div>
              </div>
          )}
        </LoadingCard>
      </DingTalkLoginContainer>
  );
}
